import { ChangeEventHandler, KeyboardEventHandler, useCallback, useRef, useState } from "react";

import { FrameStencilsApi } from ".";
import { MarkerPlayer } from "../types";
import { sliceItemUpdate } from "../utils";

interface Position {
  x: number;
  y: number;
}

export interface OnEditPlayerLabelProps {
  position: Position;
  player: MarkerPlayer;
}

export type OnEditPlayerLabelFunc = (props: OnEditPlayerLabelProps) => void;

export interface UseEditPlayerLabelApiProps {
  updateFrameStencils: FrameStencilsApi["updateFrameStencils"];
}
export const useEditPlayerLabelApi = ({ updateFrameStencils }: UseEditPlayerLabelApiProps) => {
  const playerInputRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [playerEditing, setPlayerEditing] = useState<MarkerPlayer | null>(null);
  const [playerInputPosition, setPlayerInputPosition] = useState<{ x: number; y: number } | null>(null);

  const onEditPlayerLabel = useCallback<OnEditPlayerLabelFunc>(({ position, player }) => {
    setPlayerInputPosition(position);
    setIsEditing(true);
    setPlayerEditing(player);

    requestAnimationFrame(() => {
      if (playerInputRef.current) {
        playerInputRef.current.select();
      }
    });
  }, []);

  const handleInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setPlayerEditing((prevPlayer) => {
      if (prevPlayer) {
        return { ...prevPlayer, label: event.target.value };
      }

      return prevPlayer;
    });
  }, []);

  const savePlayerEdit = useCallback(() => {
    setIsEditing(false);
    if (playerEditing) {
      updateFrameStencils((prevStencils) => {
        if (!prevStencils || playerEditing?.label?.length === 0) return prevStencils;
        return sliceItemUpdate({ item: playerEditing, arr: prevStencils });
      });
    }
    setPlayerInputPosition(null);
    setPlayerEditing(null);
  }, [playerEditing, updateFrameStencils]);

  const discardPlayerEdit = useCallback(() => {
    setIsEditing(false);
    setPlayerInputPosition(null);
    setPlayerEditing(null);
  }, []);

  const handleInputBlur = useCallback(() => {
    if (playerEditing?.label?.length === 0) discardPlayerEdit();
    else savePlayerEdit();
  }, [savePlayerEdit, playerEditing, discardPlayerEdit]);

  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (event) => {
      if (event.key === "Enter") {
        if (playerEditing?.label?.length === 0) discardPlayerEdit();
        else savePlayerEdit();
      }
      if (event.key === "Escape") {
        discardPlayerEdit();
      }
    },
    [discardPlayerEdit, savePlayerEdit, playerEditing]
  );

  return {
    playerInputRef,
    position: playerInputPosition,
    playerEditing,
    isEditing,
    onEditPlayerLabel,
    handleInputChange,
    handleInputBlur,
    handleKeyDown,
  };
};

export type EditPlayerLabelApi = ReturnType<typeof useEditPlayerLabelApi>;

export const DEFAULT_EDIT_PLAYER_LABEL_API: EditPlayerLabelApi = {
  isEditing: false,
  playerEditing: null,
  position: null,
  playerInputRef: { current: null },
  handleInputBlur: () => undefined,
  handleInputChange: () => undefined,
  handleKeyDown: () => undefined,
  onEditPlayerLabel: () => undefined,
};
